import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  useTheme,
} from "@mui/material";

import CoreInput from "../../core/CoreInput";
import { chatService } from "../../../../services";
import { useChatStore } from "../../../../hooks/useChatStore";
import useNotifier from "../../../../hooks/useNotifier";
import { IRenameFileTarget } from "../../../../types/chat";

interface RenameFileDialogProps {
  target: IRenameFileTarget | null;
  chatId: string;
  onClose: () => void;
  onConfirmCallback: (newName: string) => void;
}

const RenameFileDialog: React.FC<RenameFileDialogProps> = ({
  target,
  chatId,
  onClose,
  onConfirmCallback,
}) => {
  const notifier = useNotifier();
  const theme = useTheme();
  const chatStore = useChatStore();

  const [inputValue, setInputValue] = useState("");
  const [isOpInProgress, setIsOpInProgress] = useState(false);

  const isOpen = !!target;
  const entityLabel = target?.kind === "letter" ? "letter" : "file";

  useEffect(() => {
    if (target) {
      setInputValue(target.currentName);
    }
  }, [target]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();

    if (e.key === "Enter") {
      e.preventDefault();
      handleConfirm();
    }
  };

  const handleClose = () => {
    onClose();
    setInputValue("");
    setIsOpInProgress(false);
  };

  const handleConfirm = async () => {
    const newName = inputValue.trim();
    if (!target || !newName || isOpInProgress) {
      return;
    }

    setIsOpInProgress(true);
    try {
      const response =
        target.kind === "letter"
          ? await chatStore.updateChatLetterName(target.letter, newName)
          : await chatService.renameFile(chatId, target.file.id, newName);

      if (response.success) {
        notifier.showSuccess(`Successfully renamed the ${entityLabel}.`);
        onConfirmCallback(newName);
        handleClose();
        return;
      }

      notifier.showError(
        response.error ||
          `Failed to rename the ${entityLabel}. Please try again.`,
      );
    } catch {
      notifier.showError(
        `Failed to rename the ${entityLabel}. Please try again.`,
      );
    } finally {
      setIsOpInProgress(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          fontSize: "19px",
        }}
      >
        Rename {entityLabel === "letter" ? "Letter" : "File"}
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2 }}>
        <Box
          sx={{
            paddingTop: 2,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Typography
            sx={{
              color: theme.palette.text.secondary,
              lineHeight: 1.75,
            }}
          >
            Enter a new name for this {entityLabel}:
          </Typography>

          <CoreInput
            fullWidth
            autoFocus
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={`Enter ${entityLabel} name`}
            disabled={isOpInProgress}
            inputProps={{
              maxLength: 150,
            }}
            sx={{
              "& .MuiInputBase-root": {
                height: "48px",
              },
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1.5,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={isOpInProgress}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={isOpInProgress || !inputValue.trim()}
          loading={isOpInProgress}
        >
          Rename
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RenameFileDialog;
